<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import { ButtonV2, Icon, Text } from '@Shelly/shelly-design-system';

	type EmWidgetStateKind = 'empty' | 'setup' | 'error';

	export let kind: EmWidgetStateKind = 'empty';
	export let title = '';
	export let message = '';
	export let minHeight = '160px';
	export let onRefresh: (() => void) | null | undefined = null;
	export let actionHref = '';
	export let actionLabel = '';
	export let compact = false;

	const normalizeStateText = (value: string) =>
		value
			.trim()
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.replace(/[.!?]+$/, '');

	$: resolvedTitle =
		title ||
		(kind === 'setup'
			? $format('energy_monitoring_state_setup_title', { default: 'Setup needed' })
			: kind === 'error'
				? $format('energy_monitoring_state_error_title', { default: 'Unable to load data' })
				: $format('energy_monitoring_state_empty_title', {
						default: 'No activity to display',
					}));
	$: requestedMessage =
		message ||
		(kind === 'error'
			? $format('energy_monitoring_state_error_message', {
					default: 'Something went wrong. Try again.',
				})
			: '');
	$: resolvedMessage =
		normalizeStateText(requestedMessage) === normalizeStateText(resolvedTitle)
			? ''
			: requestedMessage;
	$: icon =
		kind === 'setup'
			? 'fas fa-gear'
			: kind === 'error'
				? 'fas fa-triangle-exclamation'
				: 'fas fa-pause';

	const onAction = () => {
		if (actionHref) push(actionHref);
	};
</script>

<div
	class="em-widget-state em-widget-state-{kind}"
	class:em-widget-state-compact={compact}
	style={`--em-widget-state-min-height: ${compact ? 'auto' : minHeight}`}
>
	<div class="em-widget-state-icon" aria-hidden="true"><Icon {icon} size="s" /></div>
	<div class="em-widget-state-copy">
		<Text styleType="body" class="text-600 text-center">{resolvedTitle}</Text>
		{#if resolvedMessage}
			<Text styleType="body-small" class="text-color-muted text-center">{resolvedMessage}</Text>
		{/if}
	</div>
	{#if onRefresh || (actionHref && actionLabel)}
		<div class="em-widget-state-actions" data-dashboard-widget-interactive>
			{#if onRefresh}
				<ButtonV2
					size="s"
					variant="secondary"
					icon="fas fa-rotate-right"
					aria-label={kind === 'error'
						? $format('button_retry', { default: 'Retry' })
						: $format('button_refresh', { default: 'Refresh' })}
					on:click={onRefresh}
				>
					{kind === 'error'
						? $format('button_retry', { default: 'Retry' })
						: $format('button_refresh', { default: 'Refresh' })}
				</ButtonV2>
			{/if}
			{#if actionHref && actionLabel}
				<ButtonV2 size="s" variant="primary" aria-label={actionLabel} on:click={onAction}>
					{actionLabel}
				</ButtonV2>
			{/if}
		</div>
	{/if}
</div>

<style>
	.em-widget-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-s);
		flex: 1;
		min-height: var(--em-widget-state-min-height);
		padding: var(--spacing-m);
		border-radius: var(--border-radius-m, 8px);
	}

	.em-widget-state-copy {
		display: grid;
		gap: var(--spacing-xxs);
	}

	.em-widget-state-icon {
		display: grid;
		place-items: center;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		color: var(--color-muted);
		background: var(--color-2);
	}

	.em-widget-state-setup {
		background: color-mix(in srgb, var(--color-warning) 8%, transparent);
	}

	.em-widget-state-setup .em-widget-state-icon {
		color: var(--color-warning);
		background: color-mix(in srgb, var(--color-warning) 14%, transparent);
	}

	.em-widget-state-error {
		background: color-mix(in srgb, var(--color-danger) 8%, transparent);
	}

	.em-widget-state-error .em-widget-state-icon {
		color: var(--color-danger);
		background: color-mix(in srgb, var(--color-danger) 14%, transparent);
	}

	.em-widget-state-compact {
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		padding: var(--spacing-s);
	}

	.em-widget-state-compact .em-widget-state-copy {
		flex: 1;
	}

	.em-widget-state-compact :global(.text-center) {
		text-align: left !important;
	}

	.em-widget-state-actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: var(--spacing-s);
	}
</style>
